import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";

import { loadBallEntries } from "../../api/ballbyball.api";
import { aiTagMatch } from "../../api/matches.api";
import WalletBadge from "../../components/wallet/WalletBadge";

function InfoBlock({ title, data }) {
  if (!data) return null;

  const rows = Object.entries(data).filter(([, value]) => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return true;
    return true;
  });

  if (rows.length === 0) return null;

  return (
    <div className="bg-bg-secondary/40 rounded-lg p-3 border border-bg-secondary">
      <div className="text-xs uppercase tracking-wider text-text-secondary mb-2">
        {title}
      </div>
      <div className="space-y-2 text-sm">
        {rows.map(([key, value]) => (
          <div key={key}>
            <div className="text-text-secondary">
              {key.replaceAll("_", " ")}
            </div>
            <div className="text-text-primary break-words">
              {typeof value === "object"
                ? JSON.stringify(value, null, 2)
                : String(value)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BallEntriesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [tagging, setTagging] = useState(false);

  const runAiTag = async () => {
    if (tagging) return;
    if (!window.confirm("Run AI Tag on this match? Costs 15 Zencredits and fills missing analytics across every ball.")) return;
    setTagging(true);
    try {
      const res = await aiTagMatch(id, { fill_missing_only: true, max: 200 });
      const { tagged = 0, skipped = 0, error } = res.data || {};
      if (error) alert(`AI Tag error: ${error}`);
      else {
        alert(`AI tagged ${tagged} balls (${skipped} already complete).`);
        fetchEntries(true);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || e.message || "AI Tag failed");
    } finally {
      setTagging(false);
    }
  };

  const fetchEntries = async (isRefresh = false) => {
    if (!id) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");

    try {
      const res = await loadBallEntries(id);
      setEntries(res.data.entries || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load ball entries");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEntries(false);
  }, [id]);

  return (
    <div className="min-h-screen bg-bg-main text-text-primary">
      <div className="sticky top-0 z-10 bg-bg-primary border-b border-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-2 rounded-lg bg-bg-secondary text-text-primary"
            >
              Back
            </button>
            <div>
              <div className="text-lg font-semibold">Ball Entries</div>
              <div className="text-xs text-text-secondary">
                Full per-ball record for this match.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <WalletBadge />
            <button
              onClick={runAiTag}
              disabled={tagging}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25 disabled:opacity-50 text-xs uppercase tracking-wider"
              title="Run AI Tag on this match — fills missing fields. 15 Zencredits."
            >
              <Sparkles className="w-3.5 h-3.5" />
              {tagging ? "Tagging…" : "AI Tag · 15 ZC"}
            </button>
            <button
              onClick={() => fetchEntries(true)}
              disabled={refreshing}
              className="px-4 py-2 rounded-lg bg-button-primary text-white disabled:opacity-60"
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {loading ? (
          <div className="text-text-secondary">Loading entries...</div>
        ) : error ? (
          <div className="text-red-400">{error}</div>
        ) : entries.length === 0 ? (
          <div className="text-text-secondary">No ball entries recorded yet.</div>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="bg-bg-primary rounded-2xl p-4 border border-bg-secondary shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="text-lg font-semibold">
                      Innings {entry.innings.innings_number} - Ball {entry.sequence.display}
                    </div>
                    <div className="text-xs text-text-secondary">
                      #{entry.sequence.ball_number} - Created {entry.created_at ? new Date(entry.created_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {entry.participants.batting_team?.short_name ||
                      entry.participants.batting_team?.name}{" "}
                    vs{" "}
                    {entry.participants.bowling_team?.short_name ||
                      entry.participants.bowling_team?.name}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  
                  <InfoBlock title="Participants" data={entry.participants} />
                  <InfoBlock title="Scoring" data={entry.scoring} />
                  <InfoBlock title="Wicket" data={entry.wicket} />
                  <InfoBlock title="Analytics" data={entry.analytics} />
                  <InfoBlock title="Spatial" data={entry.spatial} />
                  <InfoBlock title="Release" data={entry.release} />
                  <InfoBlock title="Fielding" data={entry.fielding} />
                  <InfoBlock title="DRS" data={entry.drs} />
                  <InfoBlock title="Video" data={entry.video} />
                  <InfoBlock title="Trajectory" data={{ points: entry.trajectory }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
